import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import { formatActiveTotalBadge } from '@/utils/filter/cascadingFilters';
import cx from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import styles from '../Input.module.css';
import TreeSelectRows from './Components/TreeSelectRows/TreeSelectRows';
import { useTreeSelect } from './hooks/useTreeSelect';
import { TreeSelectProps } from './TreeSelect.types';

export default function TreeSelect<T extends string>({
  value,
  onChange,
  options,
  isLoading,
  placeholder,
  searchable,
  disabled,
  label,
  tooltip,
  alignment = 'vertical',
  adobeEventTrackFunctions,
  onDropdownToggle,
  'data-testid': dataTestId,
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  className,
  collapseSingleChild,
  selectionMode = 'standard',
  size = 'L',
  hideCheckboxes = false,
  clearable = true,
  nestedKeySort = 'alphabetical',
  actionBarProps,
}: TreeSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');

  const dropdownRef = useOnClickOutside(() => {
    if (isOpen) onDropdownToggle?.(false);
    setIsOpen(false);
  });

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const {
    fixedOptions,
    disabledPathSet,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    visibleRows,
    renderTreeRow,
    virtualization,
  } = useTreeSelect<T>({
    options,
    value,
    onChange,
    collapseSingleChild,
    selectionMode,
    nestedKeySort,
    hideCheckboxes,
    disabled,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setSearchInput('');
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, onDropdownToggle, setSearchInput]);

  const handleOnClearValue = useCallback(() => {
    if (!disabled) onChange([]);
  }, [disabled, onChange]);

  // Cascading filters: show `{active}/{total}` when some selected paths are disabled.
  const countLabel = useMemo(() => {
    if (!disabledPathSet?.size) return undefined;
    const total = value?.length ?? 0;
    if (total === 0) return undefined;
    const active = value.reduce((count, path) => (disabledPathSet.has(path) ? count : count + 1), 0);
    return formatActiveTotalBadge({ active, total }) ?? undefined;
  }, [disabledPathSet, value]);

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={tooltip} data-testid={dropdownLabelTestId} />
      <section className={styles.dropdownContainer}>
        <DropdownButton
          type="multiSelect"
          size={size}
          placeholder={placeholder}
          numOfValue={value?.length}
          active={isOpen}
          onClick={handleToggleDropdown}
          onClear={handleOnClearValue}
          clearable={!hideCheckboxes && clearable}
          disabled={disabled}
          countLabel={countLabel}
          data-testid={dropdownButtonTestId}
        >
          {valueLabelList.join(', ')}
        </DropdownButton>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={!!disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchInput ? searchResults.size : fixedOptions.length}
          data-testid={dropdownListTestId}
          listRef={virtualization.parentRef}
          optionsCount={options.length}
          actionBarProps={actionBarProps}
        >
          <div className={styles.treeSelectList}>
            <TreeSelectRows visibleRows={visibleRows} renderTreeRow={renderTreeRow} virtualization={virtualization} />
          </div>
        </DropdownList>
      </section>
    </div>
  );
}
